import { MessageSquareOff, Trash2 } from 'lucide-react';

import { SilencedAlertRow } from '@/react/portainer/observability/AlertsView/SilencedAlertsDatatable/types';
import { pluralize } from '@/react/common/string-utils';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  Datatable,
  TableSettingsMenu,
  TableSettingsMenuAutoRefresh,
} from '@@/datatables';
import { confirmDelete } from '@@/modals/confirm';
import { Button } from '@@/buttons';

import { createColumns } from './columns';
import { useDeleteSilence, useSilencedAlerts } from './useSilencedAlerts';
import { transformToAlertRuleRow } from './utils';

type SortKey = keyof SilencedAlertRow;

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}

const storageKey = 'portainer.silenced-alerts';
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  // default sort by 'endsAt', with the closest times first
  'endsAt',
  (set) => ({
    // 10s default refresh rate
    ...refreshableSettings(set, 10_000),
  })
);

export function SilencedAlertsDatatable() {
  // get silenced alerts and transform them into row data
  const tableState = useTableState(settingsStore, storageKey);
  const silencedAlertsQuery = useSilencedAlerts({
    refetchInterval: tableState.autoRefreshRateMS
      ? tableState.autoRefreshRateMS
      : undefined,
    select: transformToAlertRuleRow,
  });

  const deleteSilenceMutation = useDeleteSilence();

  async function handleDeleteSilence(silenceIds: string[]) {
    const confirmed = await confirmDelete(
      `Are you sure you want to delete ${pluralize(
        silenceIds.length,
        'this silence',
        'these silences'
      )}? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    await Promise.all(
      silenceIds.map((id) => {
        return deleteSilenceMutation.mutateAsync({
          silenceId: id,
          alertManagerURL: 'localhost:9093',
        });
      })
    );
  }

  const columns = createColumns((id) => handleDeleteSilence([id]));

  return (
    <Datatable
      dataset={silencedAlertsQuery.data || []}
      columns={columns}
      getGroupKey={(row, table) => {
        const sortedColumn = table
          .getAllColumns()
          .find((col) => col.getIsSorted());
        const columnDef = sortedColumn?.columnDef;

        // default sort
        if (!isColumnDef(columnDef)) {
          return row.original.environmentGroupName;
        }

        const sortKey = columnDef.accessorKey || columnDef.id;

        // default sort
        if (!isSortKey(sortKey)) {
          return row.original.environmentGroupName;
        }

        return `${row.original[sortKey]}`;
      }}
      isLoading={silencedAlertsQuery.isLoading}
      settingsManager={tableState}
      emptyContentLabel="No silenced alerts found"
      title="Silenced Alerts"
      titleIcon={MessageSquareOff}
      isRowSelectable={() => true}
      renderTableSettings={() => (
        <TableSettingsMenu>
          <TableSettingsMenuAutoRefresh
            onChange={tableState.setAutoRefreshRate}
            value={tableState.autoRefreshRateMS}
          />
        </TableSettingsMenu>
      )}
      renderTableActions={(selectedRows) => (
        <Button
          color="light"
          size="small"
          icon={Trash2}
          onClick={() => handleDeleteSilence(selectedRows.map((r) => r.id))}
          data-cy="silenced-alerts-datatable-bulk-delete-button"
          disabled={selectedRows.length === 0}
        >
          Delete
        </Button>
      )}
      data-cy="silenced-alerts-datatable"
    />
  );

  // does not work with ColumnDef typing for some reason?
  function isColumnDef(def: unknown): def is { accessorKey?: string } {
    return (
      !!def && typeof def === 'object' && ('accessorKey' in def || 'id' in def)
    );
  }

  function isSortKey(key?: string): key is SortKey {
    return (
      !!key &&
      [
        'environmentName',
        'environmentGroupName',
        'startsAt',
        'endsAt',
        'status',
        'alertRule',
      ].includes(key)
    );
  }
}
